/**
 * Cable Specification Panel
 *
 * Provides detailed cable specifications for network design including
 * cable types (DAC/AOC/Fiber), lengths, and connectors for accurate BOM.
 */

import { useState } from 'react';
import { Section } from '../layout/Section';
import { useStore } from '../../store/store';

// Cable type specifications with typical characteristics
const CABLE_TYPES = {
  DAC: {
    name: 'Direct Attach Copper (DAC)',
    maxLength: 7,
    costPerMeter: 15,
    powerPerCable: 0.5,
    description: 'Low cost, low power, limited distance',
  },
  AOC: {
    name: 'Active Optical Cable (AOC)',
    maxLength: 100,
    costPerMeter: 50,
    powerPerCable: 2,
    description: 'Medium cost, longer reach, lower weight',
  },
  FIBER: {
    name: 'Fiber Optic with Transceivers',
    maxLength: 10000,
    costPerMeter: 5,
    powerPerCable: 8,
    description: 'Flexible distance, requires transceivers',
  },
};

export function CableSpecificationPanel() {
  const { configuration, updateNetwork } = useStore();
  const [isExpanded, setIsExpanded] = useState(false);

  // Initialize cable config if not present
  const cableConfig = configuration.network.eastWest.cabling || {
    nodeToLeaf: {
      type: 'DAC',
      lengthMeters: 3,
      connector: 'QSFP112',
    },
    leafToSpine: {
      type: 'AOC',
      lengthMeters: 15,
      connector: 'QSFP112',
    },
    spineToCore: {
      type: 'FIBER',
      lengthMeters: 50,
      connector: 'QSFP112',
    },
  };

  const updateCabling = (tier: 'nodeToLeaf' | 'leafToSpine' | 'spineToCore', updates: any) => {
    updateNetwork({
      eastWest: {
        ...configuration.network.eastWest,
        cabling: {
          ...cableConfig,
          [tier]: {
            ...cableConfig[tier],
            ...updates,
          },
        },
      },
    });
  };

  // Calculate cable counts
  const { scaling, network } = configuration;
  const nodes = Math.ceil(scaling.totalGPUs / scaling.gpusPerNode);
  const nodeToLeafCount = nodes * network.eastWest.portsPerNode;

  const leafSwitchPorts = network.eastWest.leafSwitch.ports;
  const ibBlockingFactor = network.eastWest.blockingFactor || 1.0;
  const downlinksPerLeaf = Math.floor(leafSwitchPorts * ibBlockingFactor / (ibBlockingFactor + 1));
  const uplinksPerLeaf = leafSwitchPorts - downlinksPerLeaf;

  const minLeafsForCapacity = Math.ceil(nodeToLeafCount / downlinksPerLeaf);
  const minLeafsForDiversity = network.eastWest.portsPerNode;
  const leafCount = Math.max(minLeafsForCapacity, minLeafsForDiversity);
  const leafToSpineCount = leafCount * uplinksPerLeaf;

  // Calculate costs and power
  const calculateTierMetrics = (tier: keyof typeof cableConfig, count: number) => {
    const spec = cableConfig[tier];
    const typeInfo = CABLE_TYPES[spec.type as keyof typeof CABLE_TYPES];
    const totalCost = count * typeInfo.costPerMeter * spec.lengthMeters;
    const totalPower = count * typeInfo.powerPerCable;
    return { totalCost, totalPower, typeInfo };
  };

  const nodeToLeafMetrics = calculateTierMetrics('nodeToLeaf', nodeToLeafCount);
  const leafToSpineMetrics = calculateTierMetrics('leafToSpine', leafToSpineCount);

  const totalCableCost =
    nodeToLeafMetrics.totalCost + leafToSpineMetrics.totalCost;
  const totalCablePower =
    nodeToLeafMetrics.totalPower + leafToSpineMetrics.totalPower;

  return (
    <Section
      title="Cable Specifications"
      collapsible
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
    >
      <div className="space-y-4">
        {/* Node to Leaf */}
        <div className="border border-gray-300 rounded p-2">
          <div className="font-semibold text-xs mb-2">Node to Leaf Cables</div>

          <div className="space-y-2">
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Cable Type</label>
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.nodeToLeaf.type}
                onChange={(e) => updateCabling('nodeToLeaf', { type: e.target.value })}
              >
                <option value="DAC">DAC (≤7m)</option>
                <option value="AOC">AOC (≤100m)</option>
                <option value="FIBER">Fiber (any distance)</option>
              </select>
              <div className="text-[9px] text-gray-500 mt-0.5">
                {nodeToLeafMetrics.typeInfo.description}
              </div>
            </div>

            <div>
              <label className="block text-[10px] text-gray-600 mb-1">
                Length (meters)
              </label>
              <input
                type="number"
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.nodeToLeaf.lengthMeters}
                onChange={(e) =>
                  updateCabling('nodeToLeaf', { lengthMeters: parseInt(e.target.value) })
                }
                min="1"
                max={nodeToLeafMetrics.typeInfo.maxLength}
              />
            </div>

            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Connector</label>
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.nodeToLeaf.connector}
                onChange={(e) => updateCabling('nodeToLeaf', { connector: e.target.value })}
              >
                <option value="QSFP112">QSFP112 (800G)</option>
                <option value="QSFP56">QSFP56 (200G)</option>
                <option value="OSFP">OSFP (800G)</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[10px] bg-gray-50 p-2 rounded">
              <div>
                <div className="text-gray-600">Quantity</div>
                <div className="font-semibold">{nodeToLeafCount}</div>
              </div>
              <div>
                <div className="text-gray-600">Est. Cost</div>
                <div className="font-semibold">${Math.round(nodeToLeafMetrics.totalCost).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Power</div>
                <div className="font-semibold">{Math.round(nodeToLeafMetrics.totalPower)}W</div>
              </div>
            </div>
          </div>
        </div>

        {/* Leaf to Spine */}
        <div className="border border-gray-300 rounded p-2">
          <div className="font-semibold text-xs mb-2">Leaf to Spine Cables</div>

          <div className="space-y-2">
            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Cable Type</label>
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.leafToSpine.type}
                onChange={(e) => updateCabling('leafToSpine', { type: e.target.value })}
              >
                <option value="DAC">DAC (≤7m)</option>
                <option value="AOC">AOC (≤100m)</option>
                <option value="FIBER">Fiber (any distance)</option>
              </select>
              <div className="text-[9px] text-gray-500 mt-0.5">
                {leafToSpineMetrics.typeInfo.description}
              </div>
            </div>

            <div>
              <label className="block text-[10px] text-gray-600 mb-1">
                Length (meters)
              </label>
              <input
                type="number"
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.leafToSpine.lengthMeters}
                onChange={(e) =>
                  updateCabling('leafToSpine', { lengthMeters: parseInt(e.target.value) })
                }
                min="1"
                max={leafToSpineMetrics.typeInfo.maxLength}
              />
            </div>

            <div>
              <label className="block text-[10px] text-gray-600 mb-1">Connector</label>
              <select
                className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                value={cableConfig.leafToSpine.connector}
                onChange={(e) => updateCabling('leafToSpine', { connector: e.target.value })}
              >
                <option value="QSFP112">QSFP112 (800G)</option>
                <option value="QSFP56">QSFP56 (200G)</option>
                <option value="OSFP">OSFP (800G)</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-2 text-[10px] bg-gray-50 p-2 rounded">
              <div>
                <div className="text-gray-600">Quantity</div>
                <div className="font-semibold">{leafToSpineCount}</div>
              </div>
              <div>
                <div className="text-gray-600">Est. Cost</div>
                <div className="font-semibold">${Math.round(leafToSpineMetrics.totalCost).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-600">Power</div>
                <div className="font-semibold">{Math.round(leafToSpineMetrics.totalPower)}W</div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="p-2 bg-blue-50 border border-blue-200 rounded">
          <div className="font-semibold text-xs text-blue-900 mb-2">Cable Summary</div>
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-gray-600">Total Cables</div>
              <div className="font-semibold">{nodeToLeafCount + leafToSpineCount}</div>
            </div>
            <div>
              <div className="text-gray-600">Total Cable Cost</div>
              <div className="font-semibold">${Math.round(totalCableCost).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-gray-600">Total Cable Power</div>
              <div className="font-semibold">{Math.round(totalCablePower)}W</div>
            </div>
            <div>
              <div className="text-gray-600">Avg Cost/Cable</div>
              <div className="font-semibold">
                ${Math.round(totalCableCost / (nodeToLeafCount + leafToSpineCount))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
